import { z } from 'zod';
import { stripAgentInvisibleMarkup } from '../shifty/shared/markdownSanitization';

/** Presentation only. Application authority always comes from a persisted manuscript change. */
export const rewriteOptionsSchema = z.object({
  schemaVersion: z.literal(1),
  intro: z.string().max(50_000).optional(),
  originalText: z.string().max(50_000).optional(),
  options: z.array(z.object({
    text: z.string().min(1).max(50_000).refine(text => text.trim().length > 0),
    explanation: z.string().max(10_000).optional(),
  })).min(1).max(6),
});
export type RewriteOptions = z.infer<typeof rewriteOptionsSchema>;

export function parseRewriteOptions(value: unknown): RewriteOptions | null {
  const parsed = rewriteOptionsSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Plain-text fallback for older clients and content verification, never an Apply payload. */
export function rewriteOptionsAsText(block: RewriteOptions): string {
  return block.options.map((option, i) => [
    block.options.length > 1 ? `Option ${i + 1}` : '',
    option.text,
    option.explanation ?? '',
  ].filter(Boolean).join('\n\n')).join('\n\n');
}

export function rewriteOptionsCanApply(block: RewriteOptions, value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const ref = value as { changeId?: unknown; documentId?: unknown; status?: unknown; options?: unknown };
  if (typeof ref.changeId !== 'string' || !ref.changeId || typeof ref.documentId !== 'string' || !ref.documentId
    || ref.status !== 'awaiting_consent' || !Array.isArray(ref.options)) return false;
  const options = ref.options;
  return block.options.every((option, i) => options.some(candidate => candidate
    && typeof candidate.changeId === 'string' && candidate.changeId.length > 0
    && candidate.optionIndex === i
    && [candidate.suggestionText, candidate.preview?.replacementText, candidate.preview?.after].includes(option.text)));
}

/** Feedback's passage binding is separate from complete-scope approval previews. */
export const feedbackRewriteReferenceSchema = z.object({
  changeId: z.string().min(1),
  documentId: z.string().min(1),
  status: z.string(),
  changeSetId: z.string().optional(),
  proposalGroupId: z.string().optional(),
  options: z.array(z.object({
    changeId: z.string().min(1),
    optionIndex: z.number().int().nonnegative(),
    status: z.string().optional(),
    feedbackReplacementText: z.string().optional(),
  }).passthrough()),
}).passthrough();

const cleanFeedbackPassage = (value: string): string => {
  const text = stripAgentInvisibleMarkup(value).trim();
  // A dangling internal marker is not safe prose. Do not delete arbitrary words.
  return /AGENT_MARK_|<\/?annotation\b/i.test(text) ? '' : text;
};

export const prepareFeedbackRewriteOptions = (value: unknown, suggestions: unknown, reference: unknown) => {
  const block = parseRewriteOptions(value);
  const focused = z.object({ intro: z.string().optional(), suggestions: z.array(z.string()).min(1).max(6) }).safeParse(suggestions);
  const fallback = focused.success
    ? parseRewriteOptions({ ...block, schemaVersion: 1, intro: focused.data.intro ?? block?.intro,
      options: focused.data.suggestions.map((text, index) => ({ ...block?.options[index], text })) })
    : reference ? null : block;
  const parsed = feedbackRewriteReferenceSchema.safeParse(reference);
  const ref = parsed.success ? parsed.data : null;
  const source = fallback ?? block;
  if (!source) return { rewriteOptions: null, canApply: false };
  const options = source.options.map((option, index) => {
    const matches = ref?.options.filter(candidate => candidate.optionIndex === index) ?? [];
    const authoritativeText = matches.length === 1 ? matches[0].feedbackReplacementText : undefined;
    return { ...option, text: cleanFeedbackPassage(authoritativeText ?? fallback?.options[index]?.text ?? '') };
  });
  const rewriteOptions = parseRewriteOptions({ ...source, options });
  const canApply = Boolean(rewriteOptions && ref && ref.status === 'awaiting_consent'
    && ref.options.length === options.length
    && new Set(ref.options.map(option => option.changeId)).size === options.length
    && options.every((option, index) => {
      const matches = ref.options.filter(candidate => candidate.optionIndex === index);
      return matches.length === 1 && matches[0].status === 'awaiting_consent'
        && typeof matches[0].feedbackReplacementText === 'string'
        && option.text === cleanFeedbackPassage(matches[0].feedbackReplacementText);
    }));
  return { rewriteOptions, canApply };
};
